import { makeStyles } from '@material-ui/core/styles';
import { SecondaryColors, TextColors, TextOpacities } from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

const getLightTheme = (muiTheme: any) => ({
  root: {
    '& $tabs': {
      color: TextColors.dark,

      [muiTheme.breakpoints.down('sm')]: {
        '& .MuiTabs-indicator': {
          backgroundColor: SecondaryColors.dark,
        },
      },
    },
    '& $tab': {
      '&.Mui-selected': {
        [muiTheme.breakpoints.up('md')]: {
          '&:before': {
            borderBottom: `2px solid ${SecondaryColors.dark}`,
          },
        },
      },

      '&.MuiTab-textColorInherit': {
        color: hexToRGBA(TextColors.dark, TextOpacities.light2),

        '&.Mui-selected, &:hover': {
          color: TextColors.dark,
        },
      },
    },
    '& $superheading': {
      color: SecondaryColors.dark,
    },
    '& $rtc': {
      color: hexToRGBA(TextColors.dark, TextOpacities.light2),

      '& b, & strong': {
        color: TextColors.dark,
      },
    },
  },
});
const getDarkTheme = (muiTheme: any) => ({
  root: {
    '& $tabs': {
      color: TextColors.light,

      [muiTheme.breakpoints.down('sm')]: {
        '& .MuiTabs-indicator': {
          backgroundColor: SecondaryColors.main,
        },
      },
    },
    '& $tab': {
      '&.Mui-selected': {
        [muiTheme.breakpoints.up('md')]: {
          '&:before': {
            borderBottom: `2px solid ${SecondaryColors.main}`,
          },
        },
      },

      '&.MuiTab-textColorInherit': {
        color: hexToRGBA(TextColors.light, TextOpacities.dark2),

        '&.Mui-selected, &:hover': {
          color: TextColors.light,
        },
      },
    },
    '& $superheading': {
      color: SecondaryColors.main,
    },
    '& $rtc': {
      color: hexToRGBA(TextColors.light, TextOpacities.dark2),

      '& b, & strong': {
        color: TextColors.light,
      },
    },
  },
});

const useStyles = makeStyles(
  (muiTheme) => ({
    root: {
      '&.light-theme': getLightTheme(muiTheme).root,
      '&.dark-theme': getDarkTheme(muiTheme).root,
      '& .columns': {
        alignItems: 'stretch',
        alignContent: 'stretch',

        [muiTheme.breakpoints.down('sm')]: {
          display: 'flex',
          flexDirection: 'column-reverse',
        },

        '& .left-column': {
          [muiTheme.breakpoints.up('md')]: {
            width: '100%',
            flexGrow: 1,
          },
        },

        '& .right-column': {
          [muiTheme.breakpoints.up('md')]: {
            width: 'auto',
            flexShrink: 0,
          },
        },

        '& > *:not(:last-child)': {
          [muiTheme.breakpoints.down('sm')]: {
            marginBottom: 0,
          },
        },

        '& > *:not(:first-child)': {
          [muiTheme.breakpoints.down('sm')]: {
            marginBottom: muiTheme.spacing(4),
          },
        },
      },
    },
    tabs: {
      position: 'relative',
      display: 'inline-flex',

      [muiTheme.breakpoints.down('sm')]: {
        padding: '2px 0px 2px 0px',
        margin: '-2px -32px -2px -32px',
        width: 'calc(100% + 64px)',

        '& .MuiTabs-scrollable': {
          padding: '2px 2px 2px 2px',
          margin: '-2px -2px -2px -2px',
        },

        '& .MuiTabs-indicator': {
          zIndex: 1,
        },

        '& .MuiTabs-scrollButtons': {
          width: '32px',
        },

        '& .MuiTabs-flexContainer': {
          width: '100%',
        },
      },

      [muiTheme.breakpoints.up('md')]: {
        padding: '2px 2px 2px 2px',
        margin: '-2px -2px -2px -2px',
        width: 'auto',

        '& .MuiTabs-scrollable': {
          padding: '2px 2px 0 2px',
          margin: '-2px -2px 0 -2px',
        },

        '& .MuiTabs-indicator': {
          display: 'none',
        },

        '& .MuiTabs-flexContainer': {
          '&:after': {
            content: '""',
            display: 'block',
            width: '32px',
            flexShrink: 0,
          },
        },
      },

      '& .MuiTabs-scrollable': {
        display: 'inline-flex',
      },

      '& .MuiTabs-flexContainer': {
        position: 'relative',
        zIndex: 1,
      },
    },
    tabImage: {},
    tab: {
      '&.MuiTab-root': {
        backgroundColor: 'transparent',
        whiteSpace: 'nowrap',
        height: 'auto',
        padding: 0,
        [muiTheme.breakpoints.down('xs')]: {
          width: 'calc(50% - 12px)',
        },

        [muiTheme.breakpoints.down('sm')]: {
          '&:not(:last-child)': {
            marginRight: '24px',
          },
        },

        [muiTheme.breakpoints.up('md')]: {
          maxWidth: '155px',

          '&:not(:first-child)': {
            marginTop: muiTheme.spacing(3),
          },
        },

        '& img': {
          borderRadius: '4px',
        },

        '&.Mui-selected': {
          '& img': {
            borderRadius: '4px 4px 0 0',
          },

          [muiTheme.breakpoints.up('md')]: {
            '&:before': {
              content: '""',
              width: '100%',
              position: 'absolute',
              bottom: 0,
              left: 0,
              zIndex: 1,
            },
          },
        },

        '&.MuiTab-textColorInherit': {
          opacity: 0.5,

          '&.Mui-selected, &:hover': {
            opacity: 1,
          },
        },
      },
    },
    tabPanelAnimated: {
      '& > *': {
        animation: 'enter 250ms ease-out',
      },
    },
    tabPanel: {
      '& > * + *': {
        marginTop: muiTheme.spacing(2),
      },

      '&.content': {
        [muiTheme.breakpoints.up('md')]: {
          marginTop: muiTheme.spacing(3),
        },
      },
    },
    superheading: {
      '&.MuiTypography-root': {
        marginBottom: muiTheme.spacing(2),
      },
    },
    heading: {
      '& p, & h1, & h2, & h3, & h4, & h5, & h6': {
        ...muiTheme.typography.h4,
      },

      '& + *': {
        marginTop: muiTheme.spacing(2),
      },
    },
    rtc: {},
    link: {
      marginTop: muiTheme.spacing(4),
    },
    image: {
      paddingTop: '56.25%',
      position: 'relative',
      overflow: 'hidden',

      '& > *': {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
      },

      '& + *': {
        [muiTheme.breakpoints.down('sm')]: {
          marginTop: muiTheme.spacing(3),
        },
      },
    },
  }),
  { name: 'GenCTLVerticalThumbnail' },
);

export default useStyles;
